export const APP_STORE_PROVIDER_TOKEN = "128973865"

/**
 * Intent names are shared by web CTA analytics and the iOS Smart App Banner.
 * Keep these aligned with the existing mobile camera route contract.
 */
export type AppIntent =
  | "identify_grass"
  | "diagnose_lawn_problem"
  | "identify_weed"

const APP_INTENT_SCAN_MODES: Record<AppIntent, "identify" | "diagnose" | "weed"> = {
  identify_grass: "identify",
  diagnose_lawn_problem: "diagnose",
  identify_weed: "weed",
}

function campaignHash(value: string) {
  let hash = 5381
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash << 5) + hash) ^ value.charCodeAt(index)
  }
  return (hash >>> 0).toString(36).slice(0, 6)
}

/** Apple limits campaign tokens to 30 characters. Keep readable labels and a collision guard. */
export function appStoreCampaignToken(placement: string) {
  const normalized = `web_${placement}`
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "")
  if (normalized.length <= 30) return normalized
  return `${normalized.slice(0, 23)}_${campaignHash(normalized)}`
}

// Apple Custom Product Page ids (the `ppid` query param). Set per intent in
// Vercel once the CPPs exist in App Store Connect; unset vars are a no-op and
// the link falls back to the default product page. A CPP shows a different
// screenshot set per traffic intent, and App Store Connect reports its
// conversion separately.
const CPP_IDS: Partial<Record<AppIntent, string | undefined>> = {
  identify_grass: process.env.NEXT_PUBLIC_APP_STORE_PPID_IDENTIFY,
  identify_weed: process.env.NEXT_PUBLIC_APP_STORE_PPID_WEED,
  diagnose_lawn_problem: process.env.NEXT_PUBLIC_APP_STORE_PPID_DIAGNOSE,
}

function cppIdForIntent(intent: AppIntent | undefined) {
  if (!intent) return null
  const id = CPP_IDS[intent]?.trim()
  // ppids are UUIDs; reject anything else so a mis-set var cannot break the link.
  return id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)
    ? id
    : null
}

export function withAppStoreCampaign(
  destinationUrl: string,
  placement: string,
  intent?: AppIntent,
) {
  const url = new URL(destinationUrl)
  url.searchParams.set("pt", APP_STORE_PROVIDER_TOKEN)
  url.searchParams.set("ct", appStoreCampaignToken(placement))
  url.searchParams.set("mt", "8")
  const ppid = cppIdForIntent(intent)
  if (ppid) url.searchParams.set("ppid", ppid)
  return url.toString()
}

// Canonical App Store destination resolution for every web surface. Lives here
// rather than in a component so server components can import it too.
//
// The public App Store ID is a safe code fallback so a missing Vercel value
// cannot silently remove every install CTA. Set
// NEXT_PUBLIC_MOBILE_APP_PROMO_LIVE=false for an explicit kill switch;
// configured URLs/IDs still override the fallback.
// See [[obsidian/features/web-to-app-funnel]].
const IOS_APP_ID = process.env.NEXT_PUBLIC_IOS_APP_ID
const PUBLIC_IOS_APP_ID = "6775524634"

export function appStoreUrlFromId(appId: string | undefined) {
  const id = appId?.trim()
  return id && /^\d+$/.test(id) ? `https://apps.apple.com/app/id${id}` : null
}

export function normalizeAppStoreUrl(rawUrl: string | undefined) {
  const value = rawUrl?.trim()
  if (!value) return null

  try {
    const url = new URL(value)
    const pathname = url.pathname.toLowerCase()
    if (
      url.protocol !== "https:" ||
      url.hostname.toLowerCase() !== "apps.apple.com" ||
      !pathname.includes("/app/") ||
      !/\/id\d+\/?$/.test(pathname)
    ) {
      return null
    }
    return url.toString()
  } catch {
    return null
  }
}

export const APP_STORE_URL =
  normalizeAppStoreUrl(process.env.NEXT_PUBLIC_IOS_APP_STORE_URL) ??
  appStoreUrlFromId(IOS_APP_ID) ??
  appStoreUrlFromId(PUBLIC_IOS_APP_ID)

/** Kill switch only. `isAppPromoLive` is the gate a surface should render on. */
export const isAppPromoEnabled = process.env.NEXT_PUBLIC_MOBILE_APP_PROMO_LIVE !== "false"

export const isAppPromoLive = isAppPromoEnabled && Boolean(APP_STORE_URL)

/** Attributed store URL. Null when no store URL resolves; ignores the kill switch. */
export function appStoreCampaignUrl(placement: string, intent?: AppIntent) {
  if (!APP_STORE_URL) return null
  return withAppStoreCampaign(APP_STORE_URL, placement, intent)
}

/**
 * Attributed store URL for surfaces that gate on nothing else. Null when the
 * kill switch is off, so the caller can fall back to its own pre-launch route.
 */
export function appStoreUrlIfLive(placement: string) {
  return isAppPromoLive ? appStoreCampaignUrl(placement) : null
}

/**
 * Marker the app reads back off its initial URL to attribute the open.
 * mobile/lib/app-open-referrer.ts only classifies an open as Smart App Banner
 * when the URL carries exactly one `from` value, so every app-argument this
 * module emits has to carry it or the open lands in `unknown`.
 */
const SMART_BANNER_SOURCE = "smart_banner"

/**
 * Existing iOS deep-link contract used by the camera screen. This is only an
 * app-argument value for the Smart App Banner. App Store CTA hrefs continue to
 * point to Apple so a visitor without the app gets the normal install flow.
 */
export function appIntentDeepLink(intent: AppIntent) {
  return `whatgrassisthis:///scan/camera?scanMode=${APP_INTENT_SCAN_MODES[intent]}&from=${SMART_BANNER_SOURCE}`
}

/**
 * Generic app-argument for pages that carry no scan intent.
 *
 * The site has no AASA association (app/.well-known/apple-app-site-association
 * returns an empty applinks payload on purpose) and the app has no `/app`
 * route, so an https app-argument is something the app cannot resolve. The
 * custom scheme root is a route the app does have, so a banner tap from any
 * page opens the app on its home tab and still reports its source.
 */
export function appHomeDeepLink() {
  return `whatgrassisthis:///?from=${SMART_BANNER_SOURCE}`
}

/**
 * Validate a configured app-argument and return it carrying the source marker.
 *
 * Null means "not usable, fall back". A structurally safe value that is missing
 * the marker (or carries a different/duplicated `from`) is repaired rather than
 * discarded: mobile/lib/app-open-referrer.ts classifies an open as
 * `smart_banner` only when the URL holds exactly one `from=smart_banner`, so an
 * unmarked argument would honor the configuration and silently lose every
 * banner open to `unknown`.
 */
function safeSmartAppArgument(value: string, siteUrl: string) {
  try {
    const parsed = new URL(value)
    const siteHost = new URL(siteUrl).hostname.toLowerCase()
    if (parsed.protocol === "https:") {
      if (parsed.hostname.toLowerCase() !== siteHost) return null
    } else if (parsed.protocol !== "whatgrassisthis:") {
      return null
    } else if (parsed.pathname !== "" && parsed.pathname !== "/") {
      if (parsed.pathname !== "/scan/camera") return null
      const scanMode = parsed.searchParams.get("scanMode")
      if (scanMode !== "identify" && scanMode !== "diagnose" && scanMode !== "weed") {
        return null
      }
    }
    // `set` collapses duplicates, which is what the classifier requires.
    parsed.searchParams.set("from", SMART_BANNER_SOURCE)
    return parsed.toString()
  } catch {
    return null
  }
}

/**
 * Build the Smart App Banner value. Invalid public configuration fails back to
 * a deep link the app can resolve, never to a broken or unsafe app argument.
 */
export function smartAppBannerMetadata(intent?: AppIntent) {
  const appId = process.env.NEXT_PUBLIC_IOS_APP_ID?.trim()
  if (!appId || !/^\d+$/.test(appId)) return null

  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || "https://whatgrassisthis.com"
  const configuredArgument = process.env.NEXT_PUBLIC_IOS_APP_ARGUMENT_URL?.trim()
  const fallbackArgument = intent ? appIntentDeepLink(intent) : appHomeDeepLink()
  const appArgument =
    (configuredArgument && safeSmartAppArgument(configuredArgument, siteUrl)) ||
    fallbackArgument

  return [
    `app-id=${appId}`,
    `app-argument=${appArgument}`,
    `affiliate-data=pt=${APP_STORE_PROVIDER_TOKEN}&ct=${appStoreCampaignToken("smart_banner")}&mt=8`,
  ].join(", ")
}
